import { computed } from 'vue';
import { isChubbLegacyEnabled } from '@/config/chubbLegacy';
import { isEmittableCompany } from '@/components/widget/results/results-view.types';
import type { CompanyCardItem, ResultsCompany, ResultsLogoType } from '@/components/widget/results/results-view.types';
import type { CotizaTodasResponse } from '@/types/quote.types';
import type { QuoteSources } from './types';
import type { useCompanyLogos } from './useCompanyLogos';

type CompanyLogos = ReturnType<typeof useCompanyLogos>;

interface CardIdentity {
  company: ResultsCompany;
  name: string;
  logoSrc: string;
  logoType: ResultsLogoType;
}

function toCard(identity: CardIdentity, quote: CotizaTodasResponse): CompanyCardItem {
  const cobertura = quote.Cobertura;
  return {
    ...identity,
    price: cobertura.PrimaTotal,
    firstPayment: cobertura.PrimaTotalPrimerRecibo || 0,
    subsequentPayment: cobertura.PrimaTotalSubsecuentes || 0,
    subsequentCount: Math.max((cobertura.Pagos || 1) - 1, 0),
    isBestPrice: false,
    emittable: isEmittableCompany(identity.company),
  };
}

/** Flags the cheapest card; a lone card has nothing to compare against. */
export function markBestPrice(cards: CompanyCardItem[]): CompanyCardItem[] {
  if (cards.length < 2) return cards;
  const best = cards.reduce((min, card) => (card.price < min.price ? card : min));
  return cards.map((card) => ({ ...card, isBestPrice: card === best }));
}

export function useCompanyCards(sources: QuoteSources, logos: CompanyLogos) {
  const companyCards = computed<CompanyCardItem[]>(() => {
    const cards: CompanyCardItem[] = [];
    const { quotesResponse, anaQuotes, chubbQuotes } = sources;

    if (quotesResponse.value?.Cobertura) {
      cards.push(toCard(
        { company: 'QUALITAS', name: 'Quálitas', logoSrc: logos.qualitasLogo.value, logoType: 'qualitas' },
        quotesResponse.value,
      ));
    }

    if (anaQuotes.value?.Cobertura) {
      cards.push(toCard(
        { company: 'ANA', name: 'ANA Seguros', logoSrc: logos.anaLogo.value, logoType: 'ana' },
        anaQuotes.value,
      ));
    }

    if (isChubbLegacyEnabled() && chubbQuotes.value?.Cobertura) {
      cards.push(toCard(
        { company: 'CHUBB', name: 'Chubb', logoSrc: logos.chubbLogo.value, logoType: 'chubb' },
        chubbQuotes.value,
      ));
    }

    return markBestPrice(cards);
  });

  return { companyCards };
}
